import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { PermissionService } from '@core/core.public-api';
// Import profundo deliberado: el barrel completo de shared arrastra map/charts al bundle inicial.
import { IconComponent } from '@shared/ui/atoms/icon/icon.component';
import { visibleNavSections } from '../nav/nav.config';
import type { NavSection } from '../nav/nav-item.model';

/**
 * Sidenav (smart / container). Genera el menu desde NAV_SECTIONS y lo filtra por permisos:
 * un item se muestra solo si PermissionService.can(item.permission) es true. Esto es UX
 * (defensa en profundidad) — la autorizacion real la hace el backend.
 *
 * Tema "torre de control": fondo bg (mas oscuro que surface), item activo con tint de
 * primary + barra izquierda, marca arriba (monograma + subtitulo).
 */
@Component({
    selector: 'app-sidenav',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, RouterLinkActive, IconComponent],
    template: `
    <nav class="sidenav" [class.sidenav--collapsed]="collapsed()" aria-label="Navegacion principal">
      <a class="sidenav__brand" routerLink="/dashboard" aria-label="PVP Transporte — ir al dashboard">
        <span class="sidenav__monogram" aria-hidden="true">P</span>
        @if (!collapsed()) {
          <span class="sidenav__brand-text">
            <span class="sidenav__brand-name">PVP Transporte</span>
            <span class="sidenav__brand-sub">Torre de control</span>
          </span>
        }
      </a>

      @for (section of visibleSections(); track section.title) {
        <div class="sidenav__section">
          @if (!collapsed()) {
            <h2 class="sidenav__section-title">{{ section.title }}</h2>
          }
          <ul class="sidenav__list">
            @for (item of section.items; track item.path) {
              <li>
                <a
                  class="sidenav__link"
                  [routerLink]="item.path"
                  routerLinkActive="sidenav__link--active"
                  [attr.title]="item.label"
                >
                  <span class="sidenav__icon" aria-hidden="true">
                    <pvp-icon [name]="item.icon" size="md" />
                  </span>
                  @if (!collapsed()) {
                    <span class="sidenav__label">{{ item.label }}</span>
                  }
                  @if (item.sensitive && !collapsed()) {
                    <span class="sidenav__sensitive" title="Datos sensibles (auditado)">
                      <pvp-icon name="lock" size="xs" />
                    </span>
                  }
                </a>
              </li>
            }
          </ul>
        </div>
      }
    </nav>
  `,
    styles: [
        `
      .sidenav {
        height: 100%;
        background: var(--pvp-color-bg);
        border-right: 1px solid var(--pvp-color-border);
        overflow-y: auto;
        padding: 12px 8px;
      }
      .sidenav__brand {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 6px 8px 14px;
        text-decoration: none;
      }
      .sidenav__monogram {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        flex: none;
        border-radius: var(--pvp-radius-sm);
        background: var(--pvp-color-primary-tint);
        border: 1px solid var(--pvp-color-border-strong);
        color: var(--pvp-color-primary);
        font-size: 15px;
        font-weight: 700;
      }
      .sidenav__brand-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }
      .sidenav__brand-name {
        font-size: 13px;
        font-weight: 600;
        color: var(--pvp-color-text);
        white-space: nowrap;
      }
      .sidenav__brand-sub {
        font-size: 11px;
        color: var(--pvp-color-text-muted);
        white-space: nowrap;
      }
      .sidenav__section-title {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--pvp-color-text-subtle);
        margin: 14px 10px 4px;
      }
      .sidenav__list {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      .sidenav__link {
        position: relative;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 7px 10px;
        border-radius: var(--pvp-radius-sm);
        color: var(--pvp-color-text-muted);
        text-decoration: none;
        font-size: 13.5px;
        transition:
          background-color 150ms ease,
          color 150ms ease;
      }
      .sidenav__link:hover {
        background: var(--pvp-color-surface-raised);
        color: var(--pvp-color-text);
      }
      .sidenav__link--active {
        background: var(--pvp-color-primary-tint);
        color: var(--pvp-color-primary);
        font-weight: 600;
      }
      .sidenav__link--active::before {
        content: '';
        position: absolute;
        left: 0;
        top: 6px;
        bottom: 6px;
        width: 3px;
        border-radius: 3px;
        background: var(--pvp-color-primary);
      }
      .sidenav__icon {
        display: inline-flex;
        width: 20px;
        height: 20px;
        flex: none;
        align-items: center;
        justify-content: center;
      }
      .sidenav__label {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .sidenav__sensitive {
        display: inline-flex;
        margin-left: auto;
        color: var(--pvp-color-text-subtle);
      }
      .sidenav--collapsed .sidenav__brand {
        justify-content: center;
        padding-left: 0;
        padding-right: 0;
      }
      .sidenav--collapsed .sidenav__link {
        justify-content: center;
      }
    `,
    ]
})
export class SidenavComponent {
  readonly collapsed = input<boolean>(false);

  private readonly permissions = inject(PermissionService);

  /**
   * Secciones con sus items filtrados por permiso (helper compartido con el
   * dashboard — misma fuente de poda). Se evalua sincronicamente:
   * PermissionService.can() devuelve Signal<boolean>, asi que este computed se
   * recalcula si cambian los permisos (p. ej. al cambiar de tenant).
   */
  protected readonly visibleSections = computed<readonly NavSection[]>(() =>
    visibleNavSections((p) => this.permissions.can(p)()),
  );
}
